import { useEffect, useRef, useState } from "react";

import { SharedItemClient } from "@/Common/Api/SharedItem/SharedItemClient";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { SharedItemCategory } from "@/Common/Models/Portal/SharedItem/SharedItemCategory";
import { SharedItemNote } from "@/Common/Models/Portal/SharedItem/SharedItemNote";
import { PortalCommunicationComposite } from "@/Common/Models/PortalCommunication/PortalCommunicationComposite";
import { CommunicationsUIStateInbox } from "@/Common/Models/UI/Enums";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { PortalCommunicationsClient } from "@/Communications/Api/Dto/PortalCommunicationsClient";
import { usePortalCommunicationFilterSetting } from "@/Communications/CustomHook/PortalCommunication/usePortalCommunicationFilterSetting";
import { usePortalCommunicationTable } from "@/Communications/CustomHook/PortalCommunication/usePortalCommunicationTable";
import { usePortalCommunicationTableData } from "@/Communications/CustomHook/PortalCommunication/usePortalCommunicationTableData";
import { CommunicationsHelper } from "@/Communications/Helpers/CommunicationsHelper";
import { PortalCommunicationFilterSettingsType, PortalCommunicationInboxFilterSetting, PortalCommunicationSentFilterSetting } from "@/Communications/Models/PortalCommunication/PortalCommunicationFilterSettings";
import { PortalCommunicationTableSetting } from "@/Communications/Models/PortalCommunication/PortalCommunicationTableSetting";
import { Button } from "@/DesignSystem";
import { PortalCommunicationFilterSettingContainer } from "./PortalCommunicationFilterSettingContainer";
import { PortalCommunicationNotesPopup } from "./PortalCommunicationNotesPopup";

import styles from "./PortalCommunicationInbox.module.scss";

interface IPortalCommunicationInboxProps {
    inboxType: CommunicationsUIStateInbox;
    onPortalCommunicationItemClick: (SharedItemId: string) => void;
}

interface IPortalCommunicationNotesData {
    sharedItemId: string;
    sharedItemNotes: SharedItemNote[]
    topPosition: number;
    leftPosition: number;
    footerPosition: number;
}

export function PortalCommunicationInbox(props: Readonly<IPortalCommunicationInboxProps>): JSX.Element {
    const { getText } = useTranslation();

    const isTypeSent = CommunicationsHelper.isInboxTypeSent(props.inboxType);
    const [tableSetting, setTableSetting] = useState(() => communicationsStore.portalCommunicationTableSetting[props.inboxType] ?? new PortalCommunicationTableSetting());
    const { currentFilterSetting } = usePortalCommunicationFilterSetting(props.inboxType);
    const [filterSetting, setFilterSetting] = useState<PortalCommunicationFilterSettingsType>(currentFilterSetting);
    const [isFilterSettingVisible, setIsFilterSettingVisible] = useState(false);
    const [isColumnOrderControlVisible, setIsColumnOrderControlVisible] = useState(false);
    const [notesData, setNotesData] = useState<IPortalCommunicationNotesData>(null);
    const [portalCommunicationTableItems, setPortalCommunicationTableItems] = useState<TableDataSourceItem<PortalCommunicationComposite>[]>([]);
    const [sharedItemCategories, setSharedItemCategories] = useState<SharedItemCategory[]>([]);
    const { columns, tableRenderOptions, columnConfigurationModels, saveOptionKey } = usePortalCommunicationTable(
        tableSetting,
        props.inboxType,
        sharedItemCategories,
        sharedItemId => {
            setPortalCommunicationTableItems(
                p => p.map(item => {
                    if (item.Model.sharedItem.SharedItemId === sharedItemId) {
                        item.IsSelected = !item.IsSelected;
                    }

                    return item;
                })
            );
        },
        isChecked => {
            setPortalCommunicationTableItems(p => {
                const currentEmployeeContactId = sessionStore.getCurrentEmployee()?.ContactId;
                const updatedTableItems = p.map(item => {
                    if (item.Model.sharedItem?.EmployeeContactId && item.Model.sharedItem.EmployeeContactId === currentEmployeeContactId) {
                        item.IsSelected = isChecked;
                    }
                    return item;
                });

                return updatedTableItems;
            });
        },
        (newOrderType, newOrderDirection) => {
            setTableSetting(p => new PortalCommunicationTableSetting({ ...p, queryOrderType: newOrderType, queryOrderDirection: newOrderDirection }));
        },
        ((event, sharedItemId, totalNotes) => handleOpenNotesPopup(event, sharedItemId, totalNotes))
    );
    const { data, sharedItemIds, totalCount, fetchPortalCommunications } = usePortalCommunicationTableData(
        tableSetting,
        filterSetting,
        props.inboxType
    );

    const footerRef = useRef(null);

    useEffect(() => {
        $(window).trigger("resize");
    }, []);

    useEffect(() => {
        setPortalCommunicationTableItems(data.map(p => new TableDataSourceItem(p)));
    }, [data]);

    useEffect(() => {
        const getSharedItemCategories = async (): Promise<void> => {
            setSharedItemCategories(await cacheStore.getSharedItemCategories());
        };

        getSharedItemCategories();
    }, []);

    useEffect(() => {
        sessionStore.dispatch(LocalOperations.Communications.UpdatePortalCommunicationFetchedSharedItemsAndTotalCount, {
            sharedItemIds: sharedItemIds,
            totalCount: totalCount
        });
    }, [sharedItemIds, totalCount]);

    useEffect(() => {
        if (tableSetting) {
            sessionStore.dispatch(
                LocalOperations.Communications.SavePortalCommunicationTableSetting,
                {
                    tableSetting,
                    inboxType: props.inboxType
                }
            );
        }
    }, [tableSetting, props.inboxType]);

    useEffect(() => {
        const loadCategories = async (): Promise<void> => {
            await cacheStore.getSharedItemCategories();
        };
        loadCategories();
    }, []);

    const isInArchive = props.inboxType === CommunicationsUIStateInbox.Archive;

    const selectedPortalCommunicationItems = portalCommunicationTableItems.filter(p => p.IsSelected);

    const showMarkAsUnread = props.inboxType === CommunicationsUIStateInbox.Inbox || props.inboxType === CommunicationsUIStateInbox.FlaggedForReview;

    const handleMarkPortalCommunicationAsUnread = async (): Promise<void> => {
        const sharedItemIdsToUpdate = selectedPortalCommunicationItems
            .filter(p => !!p.Model.sharedItem?.DateReadByUser)
            .map(p => p.Model.sharedItem?.SharedItemId) || [];

        if (!sharedItemIdsToUpdate.length)
            return;

        const response = await SharedItemClient.MarkPortalCommunicationAsUnread(sharedItemIdsToUpdate);

        if (response) {
            await fetchPortalCommunications();
        }
    };

    const handleOpenNotesPopup = async (e: React.MouseEvent, sharedItemId: string, totalNotes: number): Promise<void> => {
        let notes: SharedItemNote[] = [];
        if (totalNotes > 0) {
            const response = await PortalCommunicationsClient.loadSharedItemNotes(sharedItemId);
            notes = response ?? [];
        }

        setNotesData({
            sharedItemId: sharedItemId,
            sharedItemNotes: notes ?? [],
            topPosition: e.clientY,
            leftPosition: e.clientX - 600,
            footerPosition: footerRef.current.getBoundingClientRect().top
        });
    };

    const handleSaveNote = (sharedItemId: string): void => {
        setNotesData(null);

        setPortalCommunicationTableItems(
            p => p.map(item => {
                if (item.Model.sharedItem.SharedItemId === sharedItemId) {
                    item.Model.numberOfSharedItemNotes = item.Model.numberOfSharedItemNotes + 1;
                }

                return item;
            })
        );
    };

    return (
        <div className="workspace">
            <div>
                <CommonTableHeaderComponent
                    title={`${getText("SharedItem_PortalCommunicationsLabel")} : ${getText(CommunicationsHelper.getLabelByCommunicationsUIStateInbox(props.inboxType))}`}
                    searchText={tableSetting.searchText}
                    searchPlaceholder={getText("SearchLabel")}
                    onSearch={text => setTableSetting(p => new PortalCommunicationTableSetting({ ...p, searchText: text }))}
                    onRefreshClick={() => fetchPortalCommunications()}
                    onSettingsClick={() => setIsFilterSettingVisible(p => !p)}
                />
                {isFilterSettingVisible && (
                    <PortalCommunicationFilterSettingContainer
                        initialFilterSetting={filterSetting}
                        inboxType={props.inboxType}
                        onColumnOrderingButtonClick={() => {
                            setIsFilterSettingVisible(false);
                            setIsColumnOrderControlVisible(true);
                        }}
                        onSave={(setting, isSaveSettingsChecked) => {
                            if (isSaveSettingsChecked) {
                                if (isTypeSent) {
                                    PortalCommunicationsClient.savePortalCommunicationSentFilterSetting(setting as PortalCommunicationSentFilterSetting);
                                }
                                else {
                                    PortalCommunicationsClient.savePortalCommunicationFilterSetting(setting as PortalCommunicationInboxFilterSetting, props.inboxType);
                                }
                            }

                            setFilterSetting(setting);
                            setTableSetting(p => new PortalCommunicationTableSetting({ ...p, currentPageNumber: 1 }));
                            setIsFilterSettingVisible(false);
                            sessionStore.dispatch(
                                LocalOperations.Communications.UpdatePortalCommunicationFilterSetting,
                                {
                                    filterSetting: setting,
                                    inboxType: props.inboxType
                                }
                            );
                        }}
                        onClose={() => setIsFilterSettingVisible(false)}
                    />
                )}
                {isColumnOrderControlVisible && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfigurationModels}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="PortalCommunicationInboxFilterSettingColumnOrderControl"
                        closeColumnOrderPopup={() => setIsColumnOrderControlVisible(false)}
                    />
                )}
            </div>
            <div id="posref" className="tb ws-scrollable">
                <div className="myle-doc-tb main-content myle-form">
                    <MyleTable
                        TableRenderOptions={tableRenderOptions}
                        TableId="portalCommunicationInboxTable"
                        Columns={columns}
                        DataSource={Immutable.List(portalCommunicationTableItems)}
                        SaveOptionskeyTarget={saveOptionKey}
                        RowClickFunction={(datasourceItem: TableDataSourceItem<PortalCommunicationComposite>) => props.onPortalCommunicationItemClick(datasourceItem.Model.sharedItem.SharedItemId)}
                    />
                    {!!notesData && (
                        <PortalCommunicationNotesPopup
                            sharedItemId={notesData.sharedItemId}
                            sharedItemNotes={notesData.sharedItemNotes}
                            topPosition={notesData.topPosition}
                            leftPosition={notesData.leftPosition}
                            footerPosition={notesData.footerPosition}
                            onSaveNote={sharedItemId => handleSaveNote(sharedItemId)}
                            onCancel={() => setNotesData(null)}
                        />
                    )}
                </div>
            </div>
            <div className={`ws-footer ${styles.inboxFooter}`} ref={footerRef}>
                <div>
                    <SimplePagerControl
                        PageSize={filterSetting.queryPageSize}
                        TotalRows={totalCount}
                        CurrentPage={tableSetting.currentPageNumber}
                        changePageFunction={nextPageNumber =>
                            setTableSetting(p =>
                                new PortalCommunicationTableSetting({ ...p, currentPageNumber: nextPageNumber })
                            )
                        }
                    />
                </div>
                <div className={styles.buttonGroupContainer}>
                    {props.inboxType !== CommunicationsUIStateInbox.Drafts && props.inboxType !== CommunicationsUIStateInbox.Sent && (
                        <Button
                            category="primary"
                            variant="brand"
                            onClick={async () => {
                                const selectedSharedItemsIds = selectedPortalCommunicationItems.map(p => p.Model.sharedItem.SharedItemId);
                                await SharedItemClient.updatePortalCommunicationsArchive(selectedSharedItemsIds, !isInArchive);
                                await fetchPortalCommunications();
                            }}
                            disabled={!selectedPortalCommunicationItems.length}
                            label={getText(isInArchive ? "RestoreLabel" : "ArchiveLabel")}
                        />
                    )}
                    {showMarkAsUnread && (
                        <Button
                            category="secondary"
                            variant="neutral"
                            onClick={() => handleMarkPortalCommunicationAsUnread()}
                            disabled={!selectedPortalCommunicationItems.length}
                            label={getText("MarkAsUnreadLabel")}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
